// src/pages/AppVersions.jsx
// Console → Apps (3.89.0, owner, Nov 5): every app and the version it's on.
// Super Admins only. The list comes from lib/appList.js: most rows update
// themselves; the App Store, Microsoft Store and test copies use
// scripts/appRelease.js. Resources → Apps (Apps.jsx) is the download page.
import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { API_BASE } from '../firebase/config';
import { SECTION_SUB } from '../theme/tabs';
import Loader from '../components/Loader';
import ShowSelect from '../components/ShowSelect';
import Pager, { usePager } from '../components/Pager';
import SortTh, { useSort } from '../components/SortTh';
import { S, when } from './Deployments';

const verNum = v => String(v || '0').split('.').reduce((t, x) => t * 1000 + Number(x), 0);

export default function AppVersions() {
  const { getToken } = useAuth();
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [show, setShow] = useState('50');
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/portal/control/apps`, { headers: { Authorization: `Bearer ${await getToken()}` } });
        const j = await r.json(); if (!r.ok) throw new Error(j.error);
        setD(j);
      } catch (e) { setError(e.message); }
    })();
  }, [getToken]);

  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (d?.rows || []).filter(r => !t || `${r.name} ${r.version} ${r.where}`.toLowerCase().includes(t));
  }, [d, q]);
  const sort = useSort(shown, { name: r => r.name, version: r => verNum(r.version), where: r => r.where || '', updated: r => r.updatedAt || '' });
  const { pageRows, pager } = usePager(sort.sorted, show);

  return (
    <div>
      <p style={{ ...SECTION_SUB, marginBottom: 14 }}>The newest app version is {d?.newest || '…'}.</p>
      {error && <div style={S.error}>{error}</div>}
      <div style={S.filters}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="App, version or where it is" style={S.input} aria-label="Search apps" />
        <ShowSelect value={show} onChange={setShow} style={S.input} labelStyle={S.showLabel} groupStyle={S.showGroup} />
      </div>
      {!d && !error ? <Loader /> : (
        <>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr style={S.thead}>
                <SortTh k="name" label="App" sort={sort.sort} onSort={sort.onSort} style={S.th}>App</SortTh>
                <SortTh k="version" label="Version" sort={sort.sort} onSort={sort.onSort} style={S.th}>Version</SortTh>
                <SortTh k="where" label="Where it is" sort={sort.sort} onSort={sort.onSort} style={S.th}>Where it is</SortTh>
                <SortTh k="updated" label="Updated" sort={sort.sort} onSort={sort.onSort} style={S.th}>Updated</SortTh>
              </tr></thead>
              <tbody>
                {pageRows.length === 0
                  ? <tr><td colSpan={4} style={{ ...S.td, textAlign: 'center', padding: 24, color: 'var(--t-78909c)' }}>No apps match.</td></tr>
                  : pageRows.map((r, i) => (
                    <tr key={r.id} style={{ ...S.tr, background: i % 2 ? 'var(--c-ffffff)' : 'var(--c-fafafa)' }}>
                      <td style={{ ...S.td, fontWeight: 600 }}>{r.name}</td>
                      <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{r.version || '—'}{r.behind && <span style={{ ...S.aud, marginLeft: 6, color: 'var(--t-b71c1c)', background: 'var(--c-fce4ec)' }}>Behind</span>}</td>
                      <td style={S.td}>{r.where}</td>
                      <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{r.updatedAt ? when(r.updatedAt) : '—'}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <Pager {...pager} label="apps" />
        </>
      )}
    </div>
  );
}
